import React, { useEffect } from "react";
import { SectionHeader } from "../Header/SectionHeader";
import InputField, { InputType } from "../input/inputField";
import { LunchPackagesOptions } from "../schoolPrices/lunchPackages/LunchPackagesOptions";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../Redux/store";
import { DropdownOptions } from "../dropdownOptions";
import { getValue } from "../../utils/object.utils";
import {
  setAdmissionFee,
  setAnnualFee,
  setExamFee,
  setExtraCirculumFee,
  setExtraClassFee,
  setLunchFee,
  setLunchPackagesId,
  setMontlyFee,
  setStationeryFee,
  setTransportationFee,
} from "../../Redux/Slices/studentAdmission.slice";
import { useGetAcademicPricesForClass } from "../../hooks/academicPrices/academicPrices.hooks";
import { Switch } from "../Switch/Switch";

export const StudentAcademicFeeForm = () => {
  const dispatch = useDispatch();

  const {
    lunchPackagedId,
    admissionFee,
    annualFee,
    extraClassFee,
    monthlyFee,
    stationeryFee,
    transportationFee,
    examFee,
    extraCirculumFee,
    lunchFee,
    classId,
  } = useSelector((state: RootState) => ({
    lunchPackagedId: state.studentAdmission.lunchPackagedId,
    admissionFee: state.studentAdmission.schoolFees.admissionFee,
    annualFee: state.studentAdmission.schoolFees.annualFee,
    extraClassFee: state.studentAdmission.schoolFees.extraClassFee,
    monthlyFee: state.studentAdmission.schoolFees.monthlyFee,
    stationeryFee: state.studentAdmission.schoolFees.stationeryFee,
    transportationFee: state.studentAdmission.schoolFees.transportationFee,
    examFee: state.studentAdmission.schoolFees.examFee,
    extraCirculumFee: state.studentAdmission.schoolFees.extraCirculumFee,
    lunchFee: state.studentAdmission.schoolFees.lunchFee,
    classId: state.studentAdmission.classId,
  }));

  const { data: adademicFeeData, refetch } = useGetAcademicPricesForClass({
    classId: classId as number,
    onSuccess: (data: any) => {
      dispatch(setAdmissionFee(getValue(data, "admission_fee")));
      dispatch(setAnnualFee(getValue(data, "annual_fee")));
      dispatch(setExamFee(getValue(data, "exam_fee")));
      dispatch(setExtraCirculumFee(getValue(data, "extra_carricular_fee")));
      // dispatch(setExtraClassFee(getValue(data, "extra_class_fee")));
      dispatch(setMontlyFee(getValue(data, "monthly_fee")));
      dispatch(setStationeryFee(getValue(data, "stationery_fee")));
    },
  });

  const handleLunchPackageSelect = (selectedLunchPackage: DropdownOptions) => {
    const lunchPackageId = getValue(selectedLunchPackage, "value");
    const lunchFee = getValue(selectedLunchPackage, "metaData.rate", 0);

    dispatch(setLunchPackagesId(lunchPackageId));
    dispatch(setLunchFee(lunchFee));
  };

  const handleExtraClassToggle = (hasEnabledExtraClass: boolean) => {
    let classExtraClassFee = getValue(adademicFeeData, "extra_class_fee");

    if (hasEnabledExtraClass) {
      classExtraClassFee = 0;
    }

    dispatch(setExtraClassFee(classExtraClassFee));
  };

  useEffect(() => {
    if (classId) {
      refetch();
    }
  }, []);

  return (
    <div>
      <div className="w-full">
        <SectionHeader
          headerText="Assign School Fees to Student"
          subText="Set up and manage the school fees that will be linked to the student during the enrollment process."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8">
          <LunchPackagesOptions
            onLunchPackageSelect={handleLunchPackageSelect}
            selectedLunchPackageId={lunchPackagedId}
          />

          <InputField
            value={lunchFee}
            setValue={(value) => {
              dispatch(setLunchFee(value));
            }}
            label="Lunch fee *"
            name="new-student-lunch-fee"
            isRequiredFocusonValidation
            type={InputType.Number}
            disabled
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8">
          <Switch
            title="Extra Class"
            description="Apply the extra class fee if the student has taken an extra class; otherwise, exclude them."
            onToggle={handleExtraClassToggle}
            isEnabled={!!extraClassFee}
          />
          <InputField
            value={extraClassFee}
            setValue={(value) => {
              dispatch(setExtraClassFee(value));
            }}
            label="Extra class fee *"
            name="new-student-extra-class-fee"
            isRequiredFocusonValidation
            type={InputType.Number}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8">
          <InputField
            value={admissionFee}
            setValue={(value) => {
              dispatch(setAdmissionFee(value));
            }}
            label="Admission fee*"
            name="new-student-admission-fee"
            isRequiredFocusonValidation
            type={InputType.Number}
          />
          <InputField
            value={annualFee}
            setValue={(value) => {
              dispatch(setAnnualFee(value));
            }}
            label="Annual fee*"
            name="new-student-annual-fee"
            isRequiredFocusonValidation
            type={InputType.Number}
          />

          <InputField
            value={monthlyFee}
            setValue={(value) => {
              dispatch(setMontlyFee(value));
            }}
            label="Monthly fee*"
            name="new-student-monthly-fee"
            isRequiredFocusonValidation
            type={InputType.Number}
          />
          <InputField
            value={stationeryFee}
            setValue={(value) => {
              dispatch(setStationeryFee(value));
            }}
            label="Stationery fee*"
            name="new-student-stationerya-fee"
            isRequiredFocusonValidation
            type={InputType.Number}
          />
          <InputField
            value={transportationFee}
            setValue={(value) => {
              dispatch(setTransportationFee(value));
            }}
            label="Transportation fee*"
            name="new-student-transportation-fee"
            isRequiredFocusonValidation
            type={InputType.Number}
          />
          <InputField
            value={examFee}
            setValue={(value) => {
              dispatch(setExamFee(value));
            }}
            label="Exam fee*"
            name="new-student-exam-fee"
            isRequiredFocusonValidation
            type={InputType.Number}
          />
          <InputField
            value={extraCirculumFee}
            setValue={(value) => {
              dispatch(setExtraCirculumFee(value));
            }}
            label="Extra cirriculam fee*"
            name="new-student-extra-cirriculam-fee"
            isRequiredFocusonValidation
            type={InputType.Number}
          />
        </div>
      </div>
    </div>
  );
};
